import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Plus, Pencil, Trash2, Smartphone, Monitor } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService, { CategoriaMenu } from '../services/api';
import {
  PageContainer,
  PageHeader,
  Button,
  Card,
  CardBody,
  Alert,
  LoadingState,
  Modal,
  FormGroup,
  FormRow,
  FormActions,
  Label,
  Input,
  Select,
  useToast,
  useConfirm,
} from '../components/ui';

const LinksRow = styled.div`
  display: flex;
  gap: var(--space-md);
  flex-wrap: wrap;
`;

const TableCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--gray-200);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const ProductImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: var(--border-radius-md);
  object-fit: cover;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: var(--space-xs);
  border-radius: var(--border-radius-sm);
  color: var(--gray-600);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);

  &.edit-btn:hover {
    background: var(--primary-50);
    color: var(--primary-700);
  }

  &.delete-btn:hover {
    background: var(--error-soft);
    color: var(--error);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--gray-700);
`;

const HintText = styled.p`
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin: var(--space-xs) 0 0;
`;

const CatFormRow = styled.form`
  display: flex;
  gap: var(--space-md);
  align-items: flex-end;
  margin-bottom: var(--space-xl);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

interface ProdottoMenu {
  id: number;
  nome: string;
  descrizione: string;
  prezzo: number;
  iva: number;
  ordine: number;
  attivo: boolean;
  categoria?: string;
  foto_url?: string;
}

const ImpostazioniMenuPage: React.FC = () => {
  const { token } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [prodotti, setProdotti] = useState<ProdottoMenu[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProdottoMenu | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [categorie, setCategorie] = useState<CategoriaMenu[]>([]);
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [newCatNome, setNewCatNome] = useState('');
  const [newCatOrdine, setNewCatOrdine] = useState(0);

  const defaultFormState = { nome: '', descrizione: '', prezzo: 0, iva: 10, ordine: 0, attivo: true, categoria: '' };
  const [formData, setFormData] = useState<Partial<ProdottoMenu>>(defaultFormState);

  const tenantConfigId = useAuth().user?.tenant_id || 1;

  useEffect(() => {
    fetchProdotti();
    fetchCategorie();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caricamento una tantum al mount
  }, []);

  const fetchCategorie = async () => {
    try {
      const cats = await apiService.getCategorieMenu();
      setCategorie(cats);
    } catch (err) {
      console.error('Errore recupero categorie:', err);
    }
  };

  const handleCreaCategoria = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCatNome.trim()) return;
    try {
      await apiService.creaCategoriaMenu({ nome: newCatNome, ordine: newCatOrdine });
      setNewCatNome('');
      setNewCatOrdine(0);
      fetchCategorie();
    } catch (err) {
      toast.error('Errore durante la creazione della categoria');
    }
  };

  const handleEliminaCategoria = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina categoria',
      message: 'Vuoi davvero eliminare questa categoria?',
      confirmLabel: 'Elimina',
      variant: 'danger'
    });
    if (!ok) return;
    try {
      await apiService.eliminaCategoriaMenu(id);
      fetchCategorie();
    } catch (err) {
      toast.error('Errore durante l\'eliminazione della categoria');
    }
  };

  const fetchProdotti = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL || '/api'}/menu`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProdotti(res.data);
    } catch (err: any) {
      console.error('Errore nel caricamento del menu:', err);
      setError('Impossibile caricare il menu. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  const openFormModal = (item?: ProdottoMenu) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      setFormData(defaultFormState);
    }
    setIsModalOpen(true);
  };

  const closeFormModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setSelectedFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key as keyof ProdottoMenu] !== undefined) {
          data.append(key, String(formData[key as keyof ProdottoMenu]));
        }
      });
      if (selectedFile) {
        data.append('foto', selectedFile);
      }

      if (editingItem) {
        await axios.put(`${process.env.REACT_APP_API_URL || '/api'}/menu/${editingItem.id}`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL || '/api'}/menu`, data, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      closeFormModal();
      fetchProdotti();
    } catch (err: any) {
      console.error('Errore salvataggio:', err);
      toast.error('Errore durante il salvataggio');
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({
      title: 'Elimina prodotto',
      message: 'Vuoi davvero eliminare (disattivare) questo prodotto?',
      confirmLabel: 'Elimina',
      variant: 'danger'
    });
    if (!ok) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL || '/api'}/menu/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProdotti();
    } catch (err: any) {
      console.error('Errore eliminazione:', err);
      toast.error('Errore durante l\'eliminazione');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState label="Caricamento menu..." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Gestione Menu e Prezzi"
        actions={
          <>
            <Button $variant="secondary" onClick={() => setIsCatModalOpen(true)}>
              Gestione Categorie
            </Button>
            <Button onClick={() => openFormModal()}>
              <Plus size={16} />
              Nuovo Prodotto
            </Button>
          </>
        }
      />

      <Card style={{ marginBottom: 'var(--space-xl)' }}>
        <CardBody>
          <h3 style={{ margin: '0 0 var(--space-md)' }}>I Tuoi Link Self-Service (Tenant {tenantConfigId})</h3>
          <p style={{ margin: '0 0 var(--space-md)', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>
            Usa questi link sui tablet del tuo negozio o nei QR Code.
          </p>
          <LinksRow>
            <Button as="a" href={`https://web.gelatogiusto.it/it/ordina?tenant_id=${tenantConfigId}`} target="_blank" rel="noreferrer">
              <Smartphone size={16} />
              Apri QR App (Stripe)
            </Button>
            <Button as="a" $variant="secondary" href={`https://web.gelatogiusto.it/it/cassa-self?tenant_id=${tenantConfigId}`} target="_blank" rel="noreferrer">
              <Monitor size={16} />
              Apri Tablet Cassa (Nexi)
            </Button>
          </LinksRow>
        </CardBody>
      </Card>

      {error && <Alert $variant="error">{error}</Alert>}

      <TableCard className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Foto</th>
              <th>Ordine</th>
              <th>Prodotto</th>
              <th>Categoria</th>
              <th>Descrizione</th>
              <th>Prezzo (€)</th>
              <th>IVA (%)</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {prodotti.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center' }}>
                  Nessun prodotto nel menu. Clicca su "Nuovo Prodotto" per iniziare.
                </td>
              </tr>
            ) : (
              prodotti.map(p => (
                <tr key={p.id} className={!p.attivo ? 'inactive-row' : ''}>
                  <td>
                    {p.foto_url ? (
                      <ProductImage
                        src={p.foto_url.startsWith('/api') || p.foto_url.startsWith('http') ? p.foto_url : `/api${p.foto_url}`}
                        alt={p.nome}
                      />
                    ) : 'N/A'}
                  </td>
                  <td>{p.ordine}</td>
                  <td style={{ fontWeight: 600 }}>{p.nome}</td>
                  <td>{p.categoria || '-'}</td>
                  <td>{p.descrizione}</td>
                  <td>€ {Number(p.prezzo).toFixed(2)}</td>
                  <td>{Number(p.iva).toFixed(0)}%</td>
                  <td>
                    <span className={`status-badge ${p.attivo ? 'active' : 'inactive'}`}>
                      {p.attivo ? 'Attivo' : 'Disattivato'}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <IconButton className="edit-btn" onClick={() => openFormModal(p)} title="Modifica" aria-label={`Modifica ${p.nome}`}>
                      <Pencil size={16} />
                    </IconButton>
                    {p.attivo && (
                      <IconButton className="delete-btn" onClick={() => handleDelete(p.id)} title="Elimina" aria-label={`Elimina ${p.nome}`}>
                        <Trash2 size={16} />
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>

      <Modal
        open={isModalOpen}
        onClose={closeFormModal}
        title={editingItem ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
        maxWidth="37.5rem"
      >
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Carica Immagine</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedFile(e.target.files ? e.target.files[0] : null)}
            />
            {editingItem?.foto_url && !selectedFile && (
              <HintText>Immagine attuale presente. Caricane un'altra per sovrascriverla.</HintText>
            )}
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Nome Prodotto *</Label>
              <Input
                type="text"
                required
                value={formData.nome || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Es. Coppetta Piccola"
              />
            </FormGroup>
            <FormGroup>
              <Label>Categoria</Label>
              <Select
                value={formData.categoria || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, categoria: e.target.value })}
              >
                <option value="">-- Seleziona o lascia vuoto --</option>
                {categorie.map(cat => (
                  <option key={cat.id} value={cat.nome}>{cat.nome}</option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Descrizione</Label>
            <Input
              type="text"
              value={formData.descrizione || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, descrizione: e.target.value })}
              placeholder="Es. Max 2 gusti"
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label>Prezzo (€) *</Label>
              <Input
                type="number"
                step="0.10"
                required
                value={formData.prezzo || 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, prezzo: parseFloat(e.target.value) })}
              />
            </FormGroup>

            <FormGroup>
              <Label>IVA (%) *</Label>
              <Input
                type="number"
                step="1"
                required
                value={formData.iva || 10}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, iva: parseFloat(e.target.value) })}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Ordine (Priorità di visualizzazione)</Label>
              <Input
                type="number"
                value={formData.ordine || 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, ordine: parseInt(e.target.value) })}
              />
            </FormGroup>

            <FormGroup style={{ justifyContent: 'center' }}>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.attivo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, attivo: e.target.checked })}
                />
                Attivo nel menu
              </CheckboxLabel>
            </FormGroup>
          </FormRow>

          <FormActions>
            <Button type="button" $variant="secondary" onClick={closeFormModal}>Annulla</Button>
            <Button type="submit">Salva</Button>
          </FormActions>
        </form>
      </Modal>

      <Modal
        open={isCatModalOpen}
        onClose={() => setIsCatModalOpen(false)}
        title="Gestione Categorie Menu"
        maxWidth="37.5rem"
      >
        <CatFormRow onSubmit={handleCreaCategoria}>
          <FormGroup style={{ flex: 2, marginBottom: 0 }}>
            <Label>Nome Categoria</Label>
            <Input type="text" required value={newCatNome} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCatNome(e.target.value)} placeholder="Es. Bevande" />
          </FormGroup>
          <FormGroup style={{ flex: 1, marginBottom: 0 }}>
            <Label>Ordine</Label>
            <Input type="number" value={newCatOrdine} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCatOrdine(parseInt(e.target.value))} />
          </FormGroup>
          <Button type="submit">Aggiungi</Button>
        </CatFormRow>

        <div className="table-container" style={{ marginBottom: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Ordine</th>
                <th>Nome Categoria</th>
                <th style={{ width: '80px', textAlign: 'center' }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {categorie.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center' }}>Nessuna categoria presente.</td></tr>
              ) : (
                categorie.map(c => (
                  <tr key={c.id}>
                    <td>{c.ordine}</td>
                    <td>{c.nome}</td>
                    <td className="actions-cell" style={{ textAlign: 'center' }}>
                      <IconButton className="delete-btn" onClick={() => handleEliminaCategoria(c.id)} title="Elimina" aria-label={`Elimina ${c.nome}`}>
                        <Trash2 size={16} />
                      </IconButton>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default ImpostazioniMenuPage;
